import { defineStore } from 'pinia'
import Delta from 'quill-delta'
import requestService from '@/services/requests'
import type { ILinearDef } from './createSurveyStore'
import { IFile } from '@/modules/a.worksheets/stores/worksheetDataStore'

type AnyObj = Record<string, any>
type ResponseEvent = 1 | 2 | 3

const RESPOND_IFACES = ['question-by-question', 'section-by-section', 'all-visible'] as const
export type RespondInterface = typeof RESPOND_IFACES[number]

export interface IQuestion {
  uuid: string
  order: number
  question_type: string
  question_text: Delta
  mandatory: boolean
  definitions: any
}

interface ISections {
  uuid: string
  title: Delta
  order: number
  send_form: boolean
  next_section: string
  questions: IQuestion[]
  completed: boolean
}

interface ISurvey {
  uuid: string
  name: string
  instructions: IFile | null
  linear_scales: ILinearDef[]
  respond_interface: RespondInterface
  sections: ISections[]
}

export interface IAnswer {
  uuid: string
  question: string
  user_answer: any | null
}

interface IStatusBarQbyq {
  answers: string[]
  current_node: IAnswer
  current_position: number
  total_questions: number
  meter_position: number
}

interface IStatusBarSbys {
  sections: string[]
  current_section: string
  current_section_position: number
  questions: string[]
  answers: IAnswer[]
  section_completed: boolean
  completed_sections: string[]
  is_survey_completed: boolean,
  is_last_question: string | null
}

interface IStatusBarAllin {
  sections: string[]
  questions: any
  answers: any
  completed_sections: string[]
  completed_questions: string[]
  questions_list: string[]
  is_survey_completed: boolean
}

const isNil = (v: unknown): v is null | undefined => v == null;
const isBlank = (v: unknown): boolean => typeof v === 'string' && v.trim() === '';


const toScaleNumber = (v: any): number | undefined => {
  const base = (typeof v === 'object') ? (v?.option ?? v?.value ?? v) : v;
  if (isNil(base) || isBlank(base) || typeof base === 'boolean') return undefined;
  const n = (typeof base === 'number') ? base
    : (typeof base === 'string' ? Number(base) : NaN);
  return Number.isFinite(n) ? n : undefined;
};

export const useUserSurveyAnswerStore = defineStore("userSurveyAnswer", {
  state: () => ({
    fromStart: false,
    can_submit: true,
    user_survey: '' as string,
    survey: {
      uuid: '',
      name: '',
      instructions: null as IFile | null,
      linear_scales: [] as ILinearDef[],
      respond_interface: 'question-by-question' as RespondInterface,
      sections: [] as ISections[],
    } as ISurvey,
    status_bar_qbyq: {
      answers: [] as string[],
      current_node: {} as IAnswer,
      current_position: 1,
      total_questions: 1,
    } as IStatusBarQbyq,
    status_bar_sbys: {
      sections: [] as string[],
      current_section: '',
      current_section_position: 1,
      questions: [] as string[],
      answers: [] as IAnswer[],
      section_completed: false,
      completed_sections: [] as string[],
      is_last_question: null
    } as IStatusBarSbys,
    status_bar_allin: {
      sections: [] as string[],
      questions: {},
      answers: {},
    } as IStatusBarAllin,
  }),

  getters: {
    respondInterface: (s) => s.survey.respond_interface,
    isQbyq: (s) => s.survey.respond_interface === 'question-by-question',
    isSbys: (s) => s.survey.respond_interface === 'section-by-section',
    isAllin: (s) => s.survey.respond_interface === 'all-visible',

    getQuestionContextByUuid: (s) => (questionUuid: string) => {
      if (!questionUuid) return null
      for (const sec of s.survey.sections || []) {
        const q = (sec.questions || []).find(q => q?.uuid === questionUuid)
        if (q) {
          return {
            sectionUuid: sec.uuid,
            sectionOrder: sec.order,
            sectionTitle: sec.title,
            questionUuid: q.uuid,
            questionTitle: q.question_text,
            definitions: q.definitions ?? null,
            section: sec,
            question: q,
          }
        }
      }
      return null
    },
  },

  actions: {

    async startSurveyFromRouter(surveyUuid: string) {
      const payloadToSend: AnyObj = { survey: surveyUuid }
      const res = await requestService.post('user-survey-start', payloadToSend)
      const body = res && 'data' in res ? (res as any).data : res
      const payload = body?.data ?? body
      const s = payload.survey ?? {}
      this.survey = {
        uuid: String(s?.uuid ?? ''),
        name: String(s?.name ?? ''),
        instructions: (s?.instructions ?? null) as IFile | null,
        linear_scales: Array.isArray(s?.linear_scales)
          ? s.linear_scales
          : (Array.isArray(s?.liner_scales) ? s.liner_scales : []),
        respond_interface: (s?.respond_interface) as RespondInterface,
        sections: Array.isArray(s?.sections) ? s.sections : [],
      }

      this.can_submit = payload.can_submit
      this.user_survey = payload.user_survey ?? ''
      this.status_bar_allin = payload.status_bar_allin ?? this.status_bar_allin
      this.status_bar_qbyq = payload.status_bar_qbyq ?? this.status_bar_qbyq
      this.status_bar_sbys = payload.status_bar_sbys ?? this.status_bar_sbys
      return payload
    },

    async startSurvey(surveyUuid: string, respondInterface: RespondInterface, surveyName?: string) {
      const payloadToSend: AnyObj = { survey: surveyUuid }
      const res = await requestService.post('user-survey-start', payloadToSend)
      const body = res && 'data' in res ? (res as any).data : res
      const payload = body?.data ?? body

      // normaliza e aplica
      const s = payload.survey ?? {}
      this.survey = {
        uuid: String(s?.uuid ?? ''),
        name: String(s?.name ?? ''),
        instructions: (s?.instructions ?? null) as IFile | null,
        linear_scales: Array.isArray(s?.linear_scales)
          ? s.linear_scales
          : (Array.isArray(s?.liner_scales) ? s.liner_scales : []),
        respond_interface: (s?.respond_interface ?? this.survey.respond_interface) as RespondInterface,
        sections: Array.isArray(s?.sections) ? s.sections : [],
      }

      this.can_submit = payload.can_submit
      this.user_survey = payload.user_survey ?? ''
      this.status_bar_allin = payload.status_bar_allin ?? this.status_bar_allin
      this.status_bar_qbyq = payload.status_bar_qbyq ?? this.status_bar_qbyq
      this.status_bar_sbys = payload.status_bar_sbys ?? this.status_bar_sbys
      return payload
    },

    // ------------ helpers p/ modelar o user_answer ------------
    getQuestionByUuid(qid: string): IQuestion | null {
      for (const sec of this.survey.sections) {
        const q = sec.questions.find(q => q.uuid === qid)
        if (q) return q
      }
      return null
    },

    shapeUserAnswer(questionUuid: string, raw: any) {
      const q = this.getQuestionByUuid(questionUuid)
      if (!q) return null

      const toText = (v: any): string => {
        if (typeof v === 'string') return v
        if (v && typeof v.text === 'string') return v.text
        return ''
      }

      const toOpt = (v: any): string | number | undefined => {
        const base = (typeof v === 'object')
          ? (v?.option ?? v?.value ?? v?.id ?? v)
          : v
        if (base === '' || base === null || base === undefined) return undefined
        if (typeof base === 'string' && base.trim() !== '' && !Number.isNaN(Number(base))) {
          return Number(base)
        }
        return base as (string | number)
      }

      const toOptArray = (v: any): Array<string | number> => {
        const arr = Array.isArray(v) ? v : (v?.options ?? [])
        const normalized = (Array.isArray(arr) ? arr : [])
          .map((x) => toOpt(x))
          .filter((x): x is string | number => x !== undefined)
        return Array.from(new Set(normalized))
      }

      switch (q.question_type) {
        case 'open': {
          const text = toText(raw).trim()
          return text.length ? { text } : null
        }
        case 'multi': {
          const option = toOpt(raw)
          return option === undefined ? null : { option }
        }
        case 'check': {
          const options = toOptArray(raw)
          return options.length ? { options } : null
        }
        case 'linear_scale': {
          const n = toScaleNumber(raw);
          if (n === undefined) return null;

          const defs = q.definitions ?? {};
          const scaleId = Number(defs?.scale_id ?? defs?.scale);
          const scale = this.survey.linear_scales.find(s => s.scale_id === scaleId);
          if (scale) {
            if (n < scale.min_scale || n > scale.max_scale) return null;
          }
          return { option: n };
        }
        default:
          return null
      }
    },
    // ----------------------------------------------------------

    async qbyq(eventType: ResponseEvent) {
      const node = this.status_bar_qbyq.current_node
      const answerUuid = node?.uuid ?? null
      const questionUuid = node?.question ?? null
      const shaped = questionUuid ? this.shapeUserAnswer(questionUuid, node?.user_answer) : null

      const formData = {
        event: eventType,
        user_survey: this.user_survey || null,
        answer: answerUuid,
        user_answer: shaped,
      }

      const res = await requestService.post2('user-survey-answers-qbyq', formData)
      const body = res && 'data' in res ? (res as any).data : res
      const data = body?.data ?? body

      if (data?.status_bar_qbyq) this.status_bar_qbyq = data.status_bar_qbyq
      if (data?.status_bar_sbys) this.status_bar_sbys = data.status_bar_sbys
      if (data?.status_bar_allin) this.status_bar_allin = data.status_bar_allin
      if (data?.survey) this.survey = {
        ...this.survey,
        ...(data.survey ?? {}),
        linear_scales: Array.isArray(data?.survey?.linear_scales)
          ? data.survey.linear_scales
          : (Array.isArray(data?.survey?.liner_scales) ? data.survey.liner_scales : this.survey.linear_scales),
      }

      return data
    },

    async sbys(eventType: ResponseEvent) {
      const formData = {
        event: eventType,
        user_survey: this.user_survey || null,
      }

      const res = await requestService.post2(
        "user-survey-answers-sbys",
        formData
      );
      const body = res && "data" in res ? (res as any).data : res;
      const data = body?.data ?? body;

      if (data?.status_bar_sbys) this.status_bar_sbys = data.status_bar_sbys
      if (data?.survey) this.survey = {
        ...this.survey,
        ...(data.survey ?? {}),
        linear_scales: Array.isArray(data?.survey?.linear_scales)
          ? data.survey.linear_scales
          : (Array.isArray(data?.survey?.liner_scales) ? data.survey.liner_scales : this.survey.linear_scales),
      }
      return data
    },

    async allin(eventType: ResponseEvent) {
      const formData = {
        event: eventType,
        user_survey: this.user_survey || null,
      }

      const res = await requestService.post2("user-survey-answers-allin", formData)
      const body = res && "data" in res ? (res as any).data : res
      const data = body?.data ?? body

      if (data?.status_bar_allin) this.status_bar_allin = data.status_bar_allin
      if (data?.status_bar_sbys) this.status_bar_sbys = data.status_bar_sbys
      if (data?.status_bar_qbyq) this.status_bar_qbyq = data.status_bar_qbyq

      if (data?.can_submit !== undefined) this.can_submit = data.can_submit
      if (data?.user_survey) this.user_survey = data.user_survey

      if (data?.survey) {
        this.survey = {
          ...this.survey,
          ...(data.survey ?? {}),
          linear_scales: Array.isArray(data?.survey?.linear_scales)
            ? data.survey.linear_scales
            : (Array.isArray(data?.survey?.liner_scales)
              ? data.survey.liner_scales
              : this.survey.linear_scales),
        }
      }

      return data
    },

    async createUptadeAnswer(answer: IAnswer) {
      const shaped = this.shapeUserAnswer(answer.question, answer.user_answer)

      const formData = {
        answer: answer.uuid ?? null,
        user_answer: shaped,
      }

      const res = await requestService.post2('create-update-survey-answer', formData)
      const body = res && 'data' in res ? (res as any).data : res
      const data = body?.data ?? body

      if (data?.status_bar_allin) this.status_bar_allin = data.status_bar_allin
      if (data?.status_bar_sbys) this.status_bar_sbys = data.status_bar_sbys
      if (data?.status_bar_qbyq) this.status_bar_qbyq = data.status_bar_qbyq
      if (data?.survey) this.survey = {
        ...this.survey,
        ...(data.survey ?? {}),
        linear_scales: Array.isArray(data?.survey?.linear_scales)
          ? data.survey.linear_scales
          : (Array.isArray(data?.survey?.liner_scales) ? data.survey.liner_scales : this.survey.linear_scales),
      }

      return data
    },
  },
});
